import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { Interval } from '../../../packages/core/src/index.ts';
import type { Formats } from '../lib/format.ts';
import type { Translate } from '../lib/i18n.ts';
import type { RangeData } from '../model/data.ts';
import type { Loadable, Store } from './store.ts';

const StoreContext = createContext<Store | null>(null);

export function StoreProvider({ store, children }: { store: Store; children: ReactNode }) {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

/** The store; the component re-renders on every change. */
export function useStore(): Store {
  const store = useContext(StoreContext);
  if (!store) throw new Error('StoreProvider missing');
  useSyncExternalStore(store.subscribe, store.getVersion, store.getVersion);
  return store;
}

export function useT(): Translate {
  return useStore().t;
}

export function useFmt(): Formats {
  return useStore().fmt;
}

/** The store's clock, updated every `intervalMs` (1 s for running clocks, a minute for reports). */
export function useNow(intervalMs = 1000): number {
  const store = useContext(StoreContext);
  const clock = store?.clock ?? Date.now;
  const [now, setNow] = useState(clock);
  useEffect(() => {
    setNow(clock());
    const id = setInterval(() => setNow(clock()), intervalMs);
    return () => clearInterval(id);
  }, [clock, intervalMs]);
  return now;
}

/**
 * Loads a time range while the component is mounted (and keeps it current with live changes). In a team it is
 * your own time; `team` asks for everyone's (owners' reports and invoices).
 */
export function useRange(range: Interval, usage = false, team = false): Loadable<RangeData> {
  const store = useStore();
  useEffect(() => store.retainRange(range.start, range.end, usage), [store, range.start, range.end, usage]);
  return store.range(range.start, range.end, usage, team);
}

export function useSession(id: string) {
  const store = useStore();
  useEffect(() => store.retainSession(id), [store, id]);
  return store.session(id);
}

/** A media query as state (layout: sidebar, drawer, phone). */
export function useMedia(query: string): boolean {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}
